/**
 * Determines whether primary Dx (ICD-10) validation is required for the
 * current single order.
 *
 * @module useIsPrimaryDiagnosisValidated
 */
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { isHealthFacilityFeatureEnabled } from "../../../data-model/healthFacility";
import { AuthOnFileRpaStatus } from "../../../enums/authOnFileAuthStatus";
import { MasterAuthStatusValue } from "../../../enums/worklistEnums";
import { RootState } from "../../../redux/store/store";

/** Breakdown of the primary Dx validation gating conditions. */
export interface PrimaryDiagnosisValidationState {
  /** Whether the facility is opted in via the `dx_validation_required` flag. */
  isFacilityOptedIn: boolean;
  /** Whether the determined auth status is ICD-applicable. */
  isAuthStatusIcdApplicable: boolean;
  /** Whether the order is not yet completed (`master_auth_status` is `new`). */
  isOrderNotCompleted: boolean;
  /** True only when all gating conditions hold. */
  isValidationRequired: boolean;
  /**
   * Whether the order can be treated as validated. When validation is not
   * required this is `true` (nothing to block). When validation is required it
   * reflects the order's `is_primary_diagnosis_validated` flag.
   */
  isValidated: boolean;
}

/**
 * Computes whether primary Dx validation is required for the current order.
 *
 * Validation is required only when ALL of the following hold:
 * - The facility is opted in via the `dx_validation_required` feature flag.
 * - The determined auth status (`auth_on_file.auth_status`) is listed in the
 *   facility's `dx_validation_auth_statuses` config.
 * - The order is not yet marked as completed, i.e. its `master_auth_status`
 *   is still `new`.
 * - The auth-on-file RPA has completed (`auth_on_file_completion_status`).
 * - At least one HPCS code is present on the order status meta.
 *
 * @returns The individual gating conditions, the combined
 *   `isValidationRequired` flag, and `isValidated` (true when validation is not
 *   required, otherwise the order's `is_primary_diagnosis_validated` flag).
 */
export const useIsPrimaryDiagnosisValidated =
  (): PrimaryDiagnosisValidationState => {
    const { generalOrderDetails, authOnFileDetails } =
      useDataAccumulatorForSingleOrder();
    const currentFacility = useSelector(
      (state: RootState) => state.singleHealthFacilityDetails.data,
    );

    return useMemo(() => {
      const isFacilityOptedIn = isHealthFacilityFeatureEnabled(
        currentFacility,
        "dx_validation_required",
      );
      const isAuthStatusIcdApplicable =
        currentFacility?.feature_config?.dx_validation_auth_statuses?.includes(
          authOnFileDetails?.auth_on_file?.auth_status ?? "",
        ) ?? false;
      const isOrderNotCompleted =
        generalOrderDetails?.order?.status?.master_auth_status ===
        MasterAuthStatusValue.New;

      const isAuthOnFileRpaCompleted =
        generalOrderDetails?.order?.status?.auth_on_file_completion_status ===
        AuthOnFileRpaStatus.COMPLETED;

      const isHpcsCodesPresent =
        (generalOrderDetails?.order?.status?.meta?.hpcs_codes?.length ?? 0) > 0;

      const isValidationRequired =
        isFacilityOptedIn &&
        isAuthStatusIcdApplicable &&
        isOrderNotCompleted &&
        isAuthOnFileRpaCompleted &&
        isHpcsCodesPresent;

      return {
        isFacilityOptedIn,
        isAuthStatusIcdApplicable,
        isOrderNotCompleted,
        isValidationRequired,
        isValidated: isValidationRequired
          ? generalOrderDetails?.order?.status
              ?.is_primary_diagnosis_validated === true
          : true,
      };
    }, [
      currentFacility,
      authOnFileDetails?.auth_on_file?.auth_status,
      generalOrderDetails?.order?.status?.master_auth_status,
      generalOrderDetails?.order?.status?.auth_on_file_completion_status,
      generalOrderDetails?.order?.status?.meta?.hpcs_codes?.length,
      generalOrderDetails?.order?.status?.is_primary_diagnosis_validated,
    ]);
  };

export default useIsPrimaryDiagnosisValidated;
